/**
 * Flutter 앱 상태바/immersive 제어
 *
 * [설계] 앱은 진입 시 상시 immersive(App.tsx) — 웹뷰가 항상 노치 뒤까지 깔린다.
 * 웹뷰 지오메트리가 고정이라 시트 열림/닫힘에 네이티브 리레이아웃이 없고, 따라서
 * 상태바 색을 따로 동기화할 필요가 없다(팝업 dim이 fixed로 노치까지 그대로 덮음).
 * 콘텐츠는 body.immersive-pad(index.css)로 노치 아래 보정, 지도만 그 클래스를 뗀다.
 */
import { isApp } from './platform'

/**
 * 웹 팝업 dim(rgba(0,0,0,0.4))이 크림 배경(#FAF6EF) 위에 깔린 결과색(참고용).
 *   (250,246,239) * 0.6 = (150,148,143) = #96948F
 */
export const DIM_OVER_CREAM = '#96948F'

/**
 * (구) 바텀 모달 열림 시 네이티브 상태바 색 동기화용.
 * 상시 immersive 도입 후 노치는 웹 dim이 직접 덮으므로 더 이상 필요 없다.
 * 다수 시트가 아직 호출하고 있어 호환을 위해 시그니처만 유지하고 동작은 비운다.
 * (호출해도 상태바 아이콘 명암이 흔들리지 않게 no-op)
 */
export const setAppStatusBarDark = (_dark: boolean, _color?: string): void => {
  // no-op — 상시 immersive가 대체함
}

/**
 * 몰입(immersive) 모드 토글 — 웹뷰가 상태바 뒤까지 깔리게.
 * 켜면 네이티브가 상태바 투명 + 상단 SafeArea 해제. (구버전 앱은 무시)
 * App.tsx에서 앱 진입 시 상시 ON으로 호출한다.
 */
export const setAppImmersive = (immersive: boolean): void => {
  if (!isApp()) return
  const channel = (window as any).FlutterChannel
  if (!channel || typeof channel.postMessage !== 'function') return
  channel.postMessage(
    JSON.stringify({
      action: 'setImmersive',
      immersive,
    })
  )
}

/**
 * 하단 몰입 토글 — 웹뷰가 홈 인디케이터 뒤까지 깔리게(하단 SafeArea 해제).
 * **센터 다이얼로그(화면 중앙 팝업)에서만** 사용 → dim이 하단 스트립까지 덮음.
 * 바텀시트는 호출하지 말 것(버튼이 인디케이터에 가려짐). 구버전 앱은 무시.
 *
 * 여러 다이얼로그가 겹칠 수 있어 카운터로 관리 — 하나라도 열려 있으면 ON.
 * acquire ↔ release를 1:1로 짝지을 것.
 */
const setAppBottomImmersiveRaw = (bottomImmersive: boolean): void => {
  if (!isApp()) return
  const channel = (window as any).FlutterChannel
  if (!channel || typeof channel.postMessage !== 'function') return
  channel.postMessage(
    JSON.stringify({
      action: 'setBottomImmersive',
      bottomImmersive,
    })
  )
}

let bottomImmersiveCount = 0

export const acquireBottomImmersive = (): void => {
  bottomImmersiveCount += 1
  if (bottomImmersiveCount === 1) setAppBottomImmersiveRaw(true)
}

export const releaseBottomImmersive = (): void => {
  if (bottomImmersiveCount === 0) return
  bottomImmersiveCount -= 1
  if (bottomImmersiveCount === 0) setAppBottomImmersiveRaw(false)
}

let noResizeCount = 0

/**
 * 키보드 시 웹뷰 리사이즈 토글 — **폼 풀페이지에서만** OFF.
 * OFF면 키보드가 웹뷰를 안 줄여서 하단 버튼이 바닥에 고정(안 밀림), 포커스 입력은 자동 스크롤.
 * **입력 있는 바텀시트에선 쓰지 말 것** — 시트가 키보드 뒤로 숨음. 구버전 앱은 무시.
 *
 * acquire ↔ release 1:1. 여러 폼이 겹칠 일은 거의 없지만 카운터로 안전하게 관리.
 */
const setKeyboardResizeRaw = (enabled: boolean): void => {
  if (!isApp()) return
  const channel = (window as any).FlutterChannel
  if (!channel || typeof channel.postMessage !== 'function') return
  channel.postMessage(
    JSON.stringify({ action: 'setKeyboardResizeEnabled', enabled })
  )
}

/**
 * 창 줄이기를 미리 꺼둔다 — 카운터는 건드리지 않는다.
 *
 * 왜 필요한가 — 실기기 측정
 * ------------------------
 * 화면에 들어가는 시점에 끄면, 앱이 그 요청을 처리하기 전에 사용자가 먼저
 * 입력칸을 탭해버린다. 20회 측정에서 앞 12회가 전부 그래서 실패했고,
 * 한 화면에 오래 머문 4회만 창이 고정돼(iH 874) 정상 동작했다.
 *
 * 그래서 '리뷰를 누르는 순간'처럼 한 발 앞선 시점에 미리 보낸다. 화면이
 * 열리고 사용자가 탭하기까지의 시간이 처리 여유가 된다.
 *
 * 카운터를 안 건드리는 이유: 짝이 되는 release가 없기 때문이다. 실제
 * 켜고 끄기는 그 화면의 useNoKeyboardResize가 맡는다.
 */
export const prewarmNoKeyboardResize = (): void => {
  setKeyboardResizeRaw(false)
}

export const acquireNoKeyboardResize = (): void => {
  noResizeCount += 1
  if (noResizeCount === 1) setKeyboardResizeRaw(false)
}

export const releaseNoKeyboardResize = (): void => {
  if (noResizeCount === 0) return
  noResizeCount -= 1
  if (noResizeCount === 0) setKeyboardResizeRaw(true)
}
